import React, { FC, useEffect, useMemo } from 'react';

import * as Sentry from '@sentry/react';

import styled from 'styled-components';
import { FiSettings } from 'react-icons/fi';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  Spinner,
  useModalContext,
  useToastContext,
} from '@resideo/blueprint-react';

import PageLayout from 'components/common/PageLayout';
import PageLoading from 'components/common/PageLoading';
import Space from 'components/common/Space';
import Typography from 'components/common/Typography';

import { useAccounts } from 'hooks/useDataStream';

import { generatePkceChallenge, getPkceStorageKey } from 'utils/pkce';

import SettingsModal from './SettingsModal';
import SignInModal from './SignIn/SignInModal';
import { FIRST_ALERT_CONNECTED_ACCOUNTS } from 'config/flags';

import { DataStreamIntroduction } from './DataStreamScreen.style';

import { PartnerCloudAccount, PartnerCloudOAuthRequest } from 'types/types';

const { Text, Title } = Typography;

const AccountName = styled(Text)`
  font-weight: 500;
  font-size: 1rem;
`;

const DeviceList = styled.ul`
  margin: 0;
  padding: 0;
`;

const DeviceInfo = styled.li`
  color: ${({ theme }) => theme.colors.faded};
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
  margin-block: 4px;
  list-style: none;
`;

const DeviceManagerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
`;

const SettingsIcon = styled(FiSettings)`
  color: ${({ theme }) => theme.colors.faded};
  margin-inline: 8px;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
  }
`;

const DataStreamScreen: FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { openModal, closeModal } = useModalContext();

  const { data: accounts, error, isFetching, isLoading } = useAccounts();

  const [searchParams, setSearchParams] = useSearchParams();

  // Always include "First Alert" in connectedAccounts when it's present in available,
  // regardless of whether it currently has connectedDevices.
  // Note: FIRST_ALERT_CONNECTED_ACCOUNTS is a static environment variable
  const connectedAccounts = useMemo(() => {
    if (!accounts) return [];
    const alreadyConnected = accounts.connected || [];

    if (!FIRST_ALERT_CONNECTED_ACCOUNTS) {
      return alreadyConnected;
    }

    const firstAlertAvailable = (accounts.available || []).find(
      a => a.partnerCloudName === 'First Alert'
    );
    const firstAlertConnected = alreadyConnected.find(
      a => a.partnerCloudName === 'First Alert'
    );

    if (firstAlertAvailable && !firstAlertConnected) {
      return [...alreadyConnected, firstAlertAvailable];
    }

    return alreadyConnected;
  }, [accounts]);

  // Filter out First Alert from available accounts list - it should only appear in connected accounts
  const availableAccounts = useMemo(() => {
    if (!accounts?.available) return [];

    // Remove First Alert from the "Connect Accounts" section when feature flag is enabled
    return accounts.available.filter(
      account =>
        !(
          FIRST_ALERT_CONNECTED_ACCOUNTS &&
          account.partnerCloudName === 'First Alert'
        )
    );
  }, [accounts]);

  const openSettingsModal = (account: PartnerCloudAccount) => {
    openModal({
      title: '',
      testAttribute: { 'data-test-data-stream-account-settings-modal': true },
      children: (
        <SettingsModal
          account={account}
          openSignInModal={signIn}
          closeModal={closeModal}
        />
      ),
    });
  };

  const openSignInModal = (
    account: PartnerCloudAccount,
    oAuthData?: PartnerCloudOAuthRequest
  ) => {
    openModal({
      title: '',
      testAttribute: { 'data-test-data-stream-account-sign-in-modal': true },
      children: (
        <SignInModal
          account={account}
          oAuthData={oAuthData}
          closeModal={closeModal}
        />
      ),
    });
  };

  const signIn = async (account: PartnerCloudAccount) => {
    // For First Alert we don't perform OAuth/sign-in. Open the device-selection modal
    // and pass the hardcoded sampleDevices so user can select/connect devices now.
    if (
      FIRST_ALERT_CONNECTED_ACCOUNTS &&
      account.partnerCloudName === 'First Alert'
    ) {
      openSignInModal(account);
      return;
    }

    if ('loginUrl' in account && account.loginUrl) {
      try {
        const { challenge, verifier } = await generatePkceChallenge();

        sessionStorage.setItem(
          getPkceStorageKey(account.partnerCloudId),
          verifier
        );

        const authUrl = new URL(account.loginUrl);

        if (import.meta.env.DEV) {
          authUrl.searchParams.set(
            'redirect_uri',
            `${window.location.href}/oauth/callback`
          );
        }

        authUrl.searchParams.append('state', account.partnerCloudId);
        authUrl.searchParams.append('code_challenge', challenge);
        authUrl.searchParams.append('code_challenge_method', 'S256');

        window.location.href = authUrl.toString();
      } catch {
        addToast({
          toastType: 'Error',
          message: t('privacy.dataStream.accountSignIn.oauth.error.generic'),
        });
      }
    } else {
      openSignInModal(account);
    }
  };

  useEffect(() => {
    const oAuthError = searchParams.get('oauth_error');

    if (oAuthError) {
      let errorMessage: string;

      switch (oAuthError) {
        case 'access_denied':
          errorMessage = t(
            'privacy.dataStream.accountSignIn.oauth.error.accessDenied'
          );
          break;

        case 'invalid_callback':
          errorMessage = t(
            'privacy.dataStream.accountSignIn.oauth.error.invalidCallback'
          );
          break;

        default:
          errorMessage = t(
            'privacy.dataStream.accountSignIn.oauth.error.generic'
          );
          break;
      }

      addToast({ toastType: 'Error', message: errorMessage });

      setSearchParams({});

      return;
    }

    const oAuthCode = searchParams.get('oauth_code');
    const partnerCloudId = searchParams.get('partner_id');

    if (oAuthCode && partnerCloudId) {
      const codeVerifier = sessionStorage.getItem(
        getPkceStorageKey(partnerCloudId)
      );

      if (!codeVerifier) {
        addToast({
          toastType: 'Error',
          message: t('privacy.dataStream.accountSignIn.oauth.error.generic'),
        });

        setSearchParams({});

        return;
      }

      const targetAccount =
        accounts?.available.find(
          acc => acc.partnerCloudId === partnerCloudId
        ) ||
        accounts?.connected.find(acc => acc.partnerCloudId === partnerCloudId);

      if (targetAccount) {
        const oAuthData = {
          code: oAuthCode,
          codeVerifier: codeVerifier,
          partnerCloudId: partnerCloudId,
        };

        sessionStorage.removeItem(getPkceStorageKey(partnerCloudId));

        setSearchParams({});

        openSignInModal(targetAccount, oAuthData);
      }
    }
  }, [accounts, searchParams, setSearchParams]);

  useEffect(() => {
    if (error) {
      addToast({
        toastType: 'Error',
        message: t('privacy.dataStream.errors.accounts.loadFailed'),
      });

      Sentry.captureEvent({
        message: '[Data Streaming] Error loading partner accounts',
        level: 'error',
      });
    }
  }, [error]);

  return (
    <PageLayout
      data-test-data-stream-screen
      title={t('privacy.dataStream.title')}>
      <DataStreamIntroduction />

      <Text marginTop='1rem'>{t('privacy.dataStream.linkYourApps')}</Text>

      <Space size={15} />

      <Flex
        data-test-available-accounts-container
        as='section'
        flexDirection='column'>
        <Title level={2}>{t('privacy.dataStream.connectAccounts')}</Title>

        {isLoading ? (
          <PageLoading />
        ) : (
          <>
            {accounts && availableAccounts.length ? (
              availableAccounts.map(account => (
                <Flex
                  data-test={`available-account-${account.partnerCloudId}`}
                  key={account.partnerCloudId}
                  alignItems='center'
                  justifyContent='space-between'
                  marginBottom='25px'>
                  <Text strong='true'>{account.partnerCloudName}</Text>
                  <Button size='small' onClick={() => signIn(account)}>
                    {t('privacy.dataStream.signIn')}
                  </Button>
                </Flex>
              ))
            ) : (
              <Text>{t('privacy.dataStream.errors.accounts.noAccounts')}</Text>
            )}
          </>
        )}
      </Flex>

      <Space size={15} />

      {accounts && connectedAccounts.length !== 0 && (
        <Flex
          data-test-connected-accounts-container
          as='section'
          flexDirection='column'>
          <Flex alignItems='center' justifyContent='space-between'>
            <Title level={2}>{t('privacy.dataStream.connectedAccounts')}</Title>
            <Link to='setup'>
              <Button size='small'>
                {t('privacy.dataStream.configureWebhook')}
              </Button>
            </Link>
          </Flex>

          {connectedAccounts.map(account => (
            <Flex
              data-test={`connected-account-${account.partnerCloudId}`}
              key={account.partnerCloudId}
              flexDirection='column'
              marginBottom='15px'>
              <Flex alignItems='center' marginBottom='4px'>
                <AccountName>{account.partnerCloudName}</AccountName>

                <DeviceManagerButton
                  onClick={() => {
                    // For First Alert: only open device selection modal if no devices are connected
                    if (
                      FIRST_ALERT_CONNECTED_ACCOUNTS &&
                      account.partnerCloudName === 'First Alert'
                    ) {
                      if (account.connectedDevices.length === 0) {
                        openSignInModal(account);
                        return;
                      }
                      openSettingsModal(account);
                      return;
                    }

                    // default behaviour for other partners
                    openSettingsModal(account);
                  }}>
                  <SettingsIcon />
                </DeviceManagerButton>
                {isFetching && <Spinner size='small' marginLeft='8px' />}
              </Flex>

              <DeviceList>
                {account.connectedDevices?.map(device => (
                  <DeviceInfo key={device.deviceId}>
                    {device.deviceType} | {device.deviceModel} |{' '}
                    {device.deviceName} | {device.deviceId}
                  </DeviceInfo>
                ))}
              </DeviceList>
            </Flex>
          ))}
        </Flex>
      )}
    </PageLayout>
  );
};

export default DataStreamScreen;
